import { useState, useEffect } from "react";
import { getApiBaseUrl } from "@/lib/api-base";

const API = getApiBaseUrl();

const box = {
  padding: "6rem 1rem 2rem",
  maxWidth: 520,
  margin: "0 auto",
  fontFamily: "system-ui, sans-serif",
  color: "#1a1a1a",
  lineHeight: 1.5,
};
const field = { display: "block", width: "100%", marginTop: 4, marginBottom: 12, padding: 8, fontSize: 16 };
const btn = {
  padding: "10px 16px",
  fontSize: 16,
  cursor: "pointer",
  background: "#8B6914",
  color: "#fff",
  border: "none",
  borderRadius: 6,
};

/**
 * Minimal Sacred Admin — no sacred-library data imports (avoids bundle crashes).
 */
export function SacredAdminStandalone() {
  const [adminKey, setAdminKey] = useState(() => localStorage.getItem("vedaPath_admin_key") || "");
  const [email, setEmail] = useState("contact.vedapath@gmail.com");
  const [password, setPassword] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [msg, setMsg] = useState("");
  const [loggedIn, setLoggedIn] = useState(() => !!localStorage.getItem("vedapath_sacred_editor_token"));
  const [serverStatus, setServerStatus] = useState(null);

  useEffect(() => {
    fetch(`${API}/admin/bootstrap/status`)
      .then((r) => r.json())
      .then(setServerStatus)
      .catch(() => setServerStatus(null));
  }, []);

  const setPasswordOnServer = async () => {
    if (!adminKey.trim()) {
      setMsg("Enter ADMIN_API_KEY from Cloudflare Worker secrets.");
      return;
    }
    if (!password || password.length < 8) {
      setMsg("Setup password must be at least 8 characters.");
      return;
    }
    setMsg("Saving…");
    localStorage.setItem("vedaPath_admin_key", adminKey.trim());
    localStorage.setItem("vedapath_admin", "true");
    const headers = { "Content-Type": "application/json", "X-Admin-Key": adminKey.trim() };
    const body = JSON.stringify({ email: email.trim(), password, role: "admin" });
    try {
      let res = await fetch(`${API}/admin/bootstrap`, { method: "POST", headers, body });
      if (res.status === 409) {
        res = await fetch(`${API}/admin/bootstrap/set-password`, { method: "POST", headers, body });
      }
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data.detail || `Setup failed (${res.status})`);
        return;
      }
      setMsg("Password set. Sign in below with the same email and password.");
      setLoginPassword(password);
    } catch (e) {
      setMsg(`Network error: ${e.message}`);
    }
  };

  const signIn = async () => {
    setMsg("Signing in…");
    try {
      const res = await fetch(`${API}/sacred-library/auth/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: email.trim(), password: loginPassword }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        setMsg(data.detail || `Login failed (${res.status})`);
        return;
      }
      localStorage.setItem("vedapath_sacred_editor_token", data.token);
      localStorage.setItem("vedapath_sacred_editor_user", JSON.stringify(data.user));
      localStorage.setItem("vedapath_admin", "true");
      setLoggedIn(true);
      setMsg(
        `Signed in as ${data.user?.email} (${data.user?.role}). Reload this page, open /library, pick a topic — Edit appears top-right. Or open /library/deities/shiva/edit directly.`
      );
    } catch (e) {
      setMsg(`Network error: ${e.message}`);
    }
  };

  const logout = () => {
    localStorage.removeItem("vedapath_sacred_editor_token");
    localStorage.removeItem("vedapath_sacred_editor_user");
    setLoggedIn(false);
    setMsg("Logged out.");
  };

  return (
    <div style={box} data-testid="sacred-admin-standalone">
      <h1 style={{ fontSize: 28, marginBottom: 8 }}>Sacred Library Admin</h1>
      <p style={{ color: "#555", marginBottom: 24 }}>
        Setup and login for content editors. If this page is blank elsewhere, use{" "}
        <a href="/sacred-admin.html">/sacred-admin.html</a> (static fallback).
      </p>

      {serverStatus && (
        <div
          style={{
            marginBottom: 20,
            padding: 12,
            borderRadius: 6,
            fontSize: 14,
            background: serverStatus.kv_bound && serverStatus.admin_key_configured ? "#ecfdf5" : "#fef3c7",
            color: "#333",
          }}
        >
          {!serverStatus.admin_key_configured && (
            <p>
              <strong>ADMIN_API_KEY not set on Worker.</strong> On your Mac run:{" "}
              <code style={{ fontSize: 12 }}>npx wrangler secret put ADMIN_API_KEY</code> in{" "}
              <code style={{ fontSize: 12 }}>backend/acharya-ai</code>, then paste the <em>same</em> value below.
            </p>
          )}
          {serverStatus.admin_key_configured && !serverStatus.kv_bound && (
            <p>
              <strong>KV not bound.</strong> Passwords may not persist. Bind VEDAPATH_KV (see DEPLOY.md) and redeploy
              Worker.
            </p>
          )}
          {serverStatus.admin_key_configured && serverStatus.kv_bound && (
            <p>Server ready: API key configured, KV bound.</p>
          )}
        </div>
      )}

      {!loggedIn && (
        <>
          <h2 style={{ fontSize: 18 }}>Step 1 — One-time password setup</h2>
          <p style={{ fontSize: 14, color: "#666" }}>
            Requires Worker secret <code>ADMIN_API_KEY</code> (wrangler secret put ADMIN_API_KEY).
          </p>
          <label>
            Admin API Key
            <input style={field} type="password" value={adminKey} onChange={(e) => setAdminKey(e.target.value)} />
          </label>
          <label>
            Admin email
            <input style={field} value={email} onChange={(e) => setEmail(e.target.value)} />
          </label>
          <label>
            New password (for login)
            <input style={field} type="password" value={password} onChange={(e) => setPassword(e.target.value)} />
          </label>
          <button type="button" style={btn} onClick={setPasswordOnServer}>
            Set password on server
          </button>

          <h2 style={{ fontSize: 18, marginTop: 32 }}>Step 2 — Sign in</h2>
          <label>
            Password
            <input
              style={field}
              type="password"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
            />
          </label>
          <button type="button" style={{ ...btn, marginRight: 8 }} onClick={signIn}>
            Sign in
          </button>
          <p style={{ fontSize: 13, marginTop: 16 }}>
            Legacy try: <strong>admin@vedapath.in</strong> / <strong>vedapath-admin</strong>
          </p>
        </>
      )}

      {loggedIn && (
        <div>
          <p style={{ marginBottom: 16 }}>
            You are logged in. <a href="/admin/pages">Universal CMS (Pages)</a> ·{" "}
            <a href="/library">Sacred Library</a> (admin/editor only) · staff use Pages CMS and submit for approval.
          </p>
          <button type="button" style={btn} onClick={logout}>
            Logout
          </button>
        </div>
      )}

      {msg && (
        <p style={{ marginTop: 20, padding: 12, background: "#f5f5f5", borderRadius: 6, fontSize: 14 }} role="status">
          {msg}
        </p>
      )}
    </div>
  );
}
